// src/hooks/useEventCallback.js
import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * Стабильная обёртка над колбэком, читающая актуальное тело
 * через ref. Ссылка на возвращаемую функцию не меняется между
 * рендерами - поэтому её можно передавать в memo-компоненты,
 * не срывая их перерисовкой родителя.
 *
 * Приём из React RFC «useEvent» (в React 19 встроенного аналога
 * пока нет). Реализация на useLayoutEffect: ref обновляется после
 * каждого рендера, до браузерной отрисовки, - раньше, чем
 * пользователь успеет вызвать обработчик.
 *
 * Применение в проекте - стабилизация колбэков, которые передаются
 * в Kanban.renderCard и в memo-карточки (TaskCardRow / ProjectCardRow).
 * Без этого каждый ре-рендер родителя создавал бы новую ссылку,
 * ломая memo у потомков (BUG-010 / R-001, R-002).
 *
 * ВАЖНО: хук НЕ подходит для функций, которые должны быть в массиве
 * зависимостей useEffect. Он стабилизирует ссылку через ref, но не
 * отслеживает изменения замыкания. Если внутри колбэка используются
 * значения из замыкания (пропсы, локальный state), useEffect с такой
 * функцией в deps не сработает при изменении этих значений.
 *
 * Пример некорректного использования:
 *   const handleSave = useEventCallback(() => save(currentFilters)); // ← сломается
 *   useEffect(() => { preview(handleSave); }, [handleSave]);          // ← никогда не перезапустится
 *
 * Пример корректного использования:
 *   const renderCard = useEventCallback((item) => <Card item={item} />);
 *   <Kanban renderCard={renderCard} />  // memo-дети не перерисовываются зря
 *
 * Для функций, реагирующих на изменение deps, используйте обычный
 * useCallback со строгим массивом зависимостей.
 */
export function useEventCallback(fn) {
  const ref = useRef(fn);
  useLayoutEffect(() => {
    ref.current = fn;
  });
  return useCallback((...args) => ref.current?.(...args), []);
}
